/**
 * Integration Selector Component
 *
 * Shows available accounting system integrations and allows connecting/disconnecting.
 * Used in the wizard Step1DataSource for the "Koppla bokföringssystem" option.
 */

import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  AlertCircle,
  RefreshCw,
  Unplug,
  Zap,
  Building2,
  Calendar,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import {
  integrationsApi,
  type IntegrationProviderInfo,
  type Integration,
  type IntegrationProvider,
  type FortnoxFinancialYear,
} from "@/lib/api";

// Provider-specific colors and logos
const PROVIDER_CONFIG: Record<IntegrationProvider, {
  bg: string;
  text: string;
  border: string;
  logo: string;
  logoBg?: string; // Optional background for logos that need contrast
}> = {
  fortnox: {
    bg: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-200",
    logo: "/logos/integrations/fortnox.svg"
  },
  visma: {
    bg: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-200",
    logo: "/logos/integrations/spiris.svg"
  },
  bjorn_lunden: {
    bg: "bg-slate-700",
    text: "text-white",
    border: "border-slate-600",
    logo: "/logos/integrations/bjorn-lunden.svg",
    logoBg: "bg-slate-700" // White logo needs dark background
  },
  pe_accounting: {
    bg: "bg-amber-50",
    text: "text-amber-600",
    border: "border-amber-200",
    logo: "/logos/integrations/pe-accounting.svg"
  },
  bokio: {
    bg: "bg-blue-50",
    text: "text-blue-600",
    border: "border-blue-200",
    logo: "/logos/integrations/bokio.png"
  },
  speedledger: {
    bg: "bg-indigo-50",
    text: "text-indigo-600",
    border: "border-indigo-200",
    logo: "/logos/integrations/speedledger.png"
  },
};

interface IntegrationSelectorProps {
  companyId: string;
  onConnected?: (integration: Integration) => void;
  onSyncComplete?: (data: unknown) => void;
  /** Set to true to force a refresh of integrations (e.g., after OAuth return) */
  forceRefresh?: boolean;
  /** Guest org.nr for validation (required for guests to sync) */
  guestOrgNumber?: string;
}

export function IntegrationSelector({
  companyId,
  onConnected: _onConnected,
  onSyncComplete,
  forceRefresh = false,
  guestOrgNumber,
}: IntegrationSelectorProps) {
  const location = useLocation();
  const [providers, setProviders] = useState<IntegrationProviderInfo[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [connectingProvider, setConnectingProvider] = useState<IntegrationProvider | null>(null);
  const [syncingIntegration, setSyncingIntegration] = useState<string | null>(null);
  const [disconnectingIntegration, setDisconnectingIntegration] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());

  // Track if we just returned from OAuth (to trigger refresh)
  const hasRefreshedForOAuthRef = useRef(false);

  // Fortnox fiscal year selection
  const [fortnoxFiscalYears, setFortnoxFiscalYears] = useState<FortnoxFinancialYear[]>([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<FortnoxFinancialYear | null>(null);
  const [loadingFiscalYears, setLoadingFiscalYears] = useState<string | null>(null);
  const [showFiscalYearSelector, setShowFiscalYearSelector] = useState<string | null>(null);

  // Check if this is a guest session (no real companyId)
  const isGuest = !companyId || companyId === "guest";

  // Guest session state (from sessionStorage after OAuth return)
  // Initialize from sessionStorage immediately to catch OAuth return
  const [guestSession, setGuestSession] = useState<{
    sessionKey: string;
    provider: IntegrationProvider;
    companyName?: string;
  } | null>(() => {
    // Only check for guest sessions when isGuest is true
    if (!companyId || companyId === "guest") {
      const sessionKey = sessionStorage.getItem("guest_integration_session");
      const provider = sessionStorage.getItem("guest_integration_provider") as IntegrationProvider | null;
      const companyName = sessionStorage.getItem("guest_integration_company");

      if (sessionKey && provider) {
        return {
          sessionKey,
          provider,
          companyName: companyName || undefined,
        };
      }
    }
    return null;
  });

  // Also check for guest session on mount/navigation (for cases where component re-renders)
  useEffect(() => {
    if (isGuest && !guestSession) {
      const sessionKey = sessionStorage.getItem("guest_integration_session");
      const provider = sessionStorage.getItem("guest_integration_provider") as IntegrationProvider | null;
      const companyName = sessionStorage.getItem("guest_integration_company");

      if (sessionKey && provider) {
        setGuestSession({
          sessionKey,
          provider,
          companyName: companyName || undefined,
        });
      }
    }
  }, [isGuest, guestSession]);

  // Detect if we just returned from OAuth (URL has source=integration)
  const isOAuthReturn = location.search.includes("source=integration");

  // Fetch providers and existing integrations
  // Also re-fetch when returning from OAuth (detected by source=integration in URL)
  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      setError(null);

      try {
        // Always fetch providers (public endpoint)
        const providersRes = await integrationsApi.getProviders();

        if (providersRes.success && providersRes.data) {
          setProviders(providersRes.data.providers || []);
        } else if (!providersRes.success) {
          console.error("Failed to fetch providers:", providersRes.error);
          setError(providersRes.error?.message || "Kunde inte hämta leverantörer");
          return; // Don't continue if providers failed
        }

        // Only fetch existing integrations for logged-in users with a real companyId
        // This is a separate try/catch so failure here doesn't block the UI
        if (!isGuest) {
          try {
            const integrationsRes = await integrationsApi.getForCompany(companyId);
            if (integrationsRes.success && integrationsRes.data) {
              setIntegrations(integrationsRes.data.integrations || []);
            }
          } catch (integrationsErr) {
            // Log but don't show error - user can still see providers and connect
            console.error("Failed to fetch existing integrations:", integrationsErr);
          }
        }
      } catch (err) {
        console.error("Failed to fetch providers:", err);
        setError("Kunde inte hämta leverantörer.");
      } finally {
        setIsLoading(false);
      }
    }

    // Run fetch if:
    // 1. Initial mount (providers.length === 0)
    // 2. companyId or isGuest changed
    // 3. We just returned from OAuth and haven't refreshed yet
    // 4. forceRefresh prop is true (e.g., after OAuth return from parent component)
    const shouldRefreshForOAuth = isOAuthReturn && !hasRefreshedForOAuthRef.current;
    if (shouldRefreshForOAuth) {
      hasRefreshedForOAuthRef.current = true;
    }

    fetchData();
  }, [companyId, isGuest, isOAuthReturn, forceRefresh]);

  // Fetch Fortnox fiscal years when integration is active
  const fetchFortnoxFiscalYears = async (integrationId: string) => {
    setLoadingFiscalYears(integrationId);
    try {
      const response = await integrationsApi.getFortnoxFinancialYears(integrationId);
      if (response.success && response.data?.financialYears) {
        const years = response.data.financialYears;
        setFortnoxFiscalYears(years);
        // Auto-select the most recent fiscal year
        if (years.length > 0) {
          // Sort by FromDate descending and pick the first one
          const sorted = [...years].sort((a, b) =>
            new Date(b.FromDate).getTime() - new Date(a.FromDate).getTime()
          );
          setSelectedFiscalYear(sorted[0]);
        }
        setShowFiscalYearSelector(integrationId);
      } else {
        toast.error(response.error?.message || "Kunde inte hämta räkenskapsår");
      }
    } catch (err) {
      console.error("Fetch fiscal years error:", err);
      toast.error("Kunde inte hämta räkenskapsår från Fortnox");
    } finally {
      setLoadingFiscalYears(null);
    }
  };

  // Handle connect - works for both guests and authenticated users
  const handleConnect = async (provider: IntegrationProvider) => {
    setConnectingProvider(provider);
    setError(null);

    try {
      // Build the return URL - include source=integration so we return to the right view
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set("source", "integration");
      const returnUrl = currentUrl.pathname + currentUrl.search;

      // Store in sessionStorage - this is the PRIMARY way to return to wizard after OAuth
      console.log("[IntegrationSelector] Setting oauth_return_url:", returnUrl);
      sessionStorage.setItem("oauth_return_url", returnUrl);

      // Verify it was set
      const verifyStored = sessionStorage.getItem("oauth_return_url");
      console.log("[IntegrationSelector] Verified sessionStorage:", verifyStored);

      let response;

      if (isGuest) {
        // Guest flow - use public endpoint (no authentication required)
        // Pass guestOrgNumber for validation after OAuth completes
        response = await integrationsApi.guestConnect(provider, returnUrl, guestOrgNumber);
      } else {
        // Authenticated user flow
        response = await integrationsApi.connect(companyId, provider, returnUrl);
      }

      if (response.success && response.data?.authUrl) {
        // Redirect to OAuth provider (same for both guests and authenticated)
        window.location.href = response.data.authUrl;
      } else {
        toast.error(response.error?.message || "Kunde inte starta anslutning");
      }
    } catch (err) {
      console.error("Connect error:", err);
      toast.error("Ett fel uppstod vid anslutning");
    } finally {
      setConnectingProvider(null);
    }
  };

  // Handle disconnect
  const handleDisconnect = async (integrationId: string) => {
    setDisconnectingIntegration(integrationId);

    try {
      const response = await integrationsApi.disconnect(integrationId);

      if (response.success) {
        // Update local state
        setIntegrations((prev) =>
          prev.map((i) =>
            i.id === integrationId ? { ...i, status: "disconnected" as const } : i
          )
        );
        toast.success("Integrationen har kopplats bort");
      } else {
        toast.error(response.error?.message || "Kunde inte koppla bort");
      }
    } catch (err) {
      console.error("Disconnect error:", err);
      toast.error("Ett fel uppstod");
    } finally {
      setDisconnectingIntegration(null);
    }
  };

  // Handle sync - for Fortnox, first show fiscal year selector
  const handleSyncClick = async (integrationId: string, provider: IntegrationProvider) => {
    if (provider === "fortnox") {
      // Fetch fiscal years first
      await fetchFortnoxFiscalYears(integrationId);
    } else {
      // Direct sync for other providers
      await handleSync(integrationId);
    }
  };

  // Perform the actual sync
  const handleSync = async (integrationId: string, fiscalYear?: { start: string; end: string }) => {
    setSyncingIntegration(integrationId);
    setShowFiscalYearSelector(null);

    try {
      console.log("[DEBUG IntegrationSelector.handleSync] Calling API sync...");
      const response = await integrationsApi.sync(integrationId, fiscalYear);

      if (response.success && response.data) {
        console.log("[DEBUG IntegrationSelector.handleSync] API success, data:", response.data);
        toast.success("Bokföringsdata hämtad!");
        onSyncComplete?.(response.data);
        console.log("[DEBUG IntegrationSelector.handleSync] onSyncComplete called");
      } else {
        console.log("[DEBUG IntegrationSelector.handleSync] API failed:", response.error);
        toast.error(response.error?.message || "Synkronisering misslyckades");
      }
    } catch (err) {
      console.error("Sync error:", err);
      toast.error("Ett fel uppstod vid synkronisering");
    } finally {
      setSyncingIntegration(null);
    }
  };

  // Guest sync - fetch data using session key
  const handleGuestSync = async (fiscalYear?: { start: string; end: string }) => {
    if (!guestSession) return;

    // SECURITY: Require org.nr for validation against accounting system
    if (!guestOrgNumber) {
      console.error("[IntegrationSelector.handleGuestSync] Missing guestOrgNumber - cannot sync");
      toast.error("Organisationsnummer saknas. Ange det i guiden först.");
      return;
    }

    setSyncingIntegration("guest");
    setShowFiscalYearSelector(null);

    try {
      console.log("[DEBUG IntegrationSelector.handleGuestSync] Calling guestSync API with org.nr validation...");
      const response = await integrationsApi.guestSync(guestSession.sessionKey, guestOrgNumber, fiscalYear);

      if (response.success && response.data) {
        console.log("[DEBUG IntegrationSelector.handleGuestSync] API success, data:", response.data);
        console.log("[DEBUG IntegrationSelector.handleGuestSync] data.type:", (response.data as { type?: string }).type);
        console.log("[DEBUG IntegrationSelector.handleGuestSync] data.data exists:", !!(response.data as { data?: unknown }).data);
        toast.success("Bokföringsdata hämtad!");
        onSyncComplete?.(response.data);
        console.log("[DEBUG IntegrationSelector.handleGuestSync] onSyncComplete called");
      } else {
        console.log("[DEBUG IntegrationSelector.handleGuestSync] API failed:", response.error);
        toast.error(response.error?.message || "Synkronisering misslyckades");
      }
    } catch (err) {
      console.error("Guest sync error:", err);
      toast.error("Ett fel uppstod vid synkronisering");
    } finally {
      setSyncingIntegration(null);
    }
  };

  // Guest disconnect - clear session
  const handleGuestDisconnect = () => {
    sessionStorage.removeItem("guest_integration_session");
    sessionStorage.removeItem("guest_integration_provider");
    sessionStorage.removeItem("guest_integration_company");
    setGuestSession(null);
    toast.success("Anslutningen har kopplats bort");
  };

  // Fetch guest fiscal years (Fortnox)
  const fetchGuestFortnoxFiscalYears = async () => {
    if (!guestSession) return;

    setLoadingFiscalYears("guest");
    try {
      const response = await integrationsApi.getGuestFiscalYears(guestSession.sessionKey);
      if (response.success && response.data?.financialYears) {
        const years = response.data.financialYears;
        setFortnoxFiscalYears(years);
        // Auto-select the most recent fiscal year
        if (years.length > 0) {
          const sorted = [...years].sort((a, b) =>
            new Date(b.FromDate).getTime() - new Date(a.FromDate).getTime()
          );
          setSelectedFiscalYear(sorted[0]);
        }
        setShowFiscalYearSelector("guest");
      } else {
        toast.error(response.error?.message || "Kunde inte hämta räkenskapsår");
      }
    } catch (err) {
      console.error("Fetch guest fiscal years error:", err);
      toast.error("Kunde inte hämta räkenskapsår från Fortnox");
    } finally {
      setLoadingFiscalYears(null);
    }
  };

  // Handle guest Fortnox sync with selected fiscal year
  const handleGuestFortnoxSync = () => {
    if (!selectedFiscalYear) {
      toast.error("Välj ett räkenskapsår");
      return;
    }
    handleGuestSync({
      start: selectedFiscalYear.FromDate,
      end: selectedFiscalYear.ToDate,
    });
  };

  // Handle Fortnox sync with selected fiscal year
  const handleFortnoxSync = (integrationId: string) => {
    if (!selectedFiscalYear) {
      toast.error("Välj ett räkenskapsår");
      return;
    }
    handleSync(integrationId, {
      start: selectedFiscalYear.FromDate,
      end: selectedFiscalYear.ToDate,
    });
  };

  // Get integration for a provider (if connected)
  const getIntegrationForProvider = (provider: IntegrationProvider): Integration | undefined => {
    return integrations.find((i) => i.provider === provider && i.status !== "disconnected");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-4">
        <div className="flex items-center gap-2 text-red-600">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  // Show message if no providers are available
  if (providers.length === 0) {
    return (
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-6 text-center">
        <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="text-amber-600" size={28} />
        </div>
        <h3 className="font-semibold text-gray-900 mb-2">
          Inga integrationer tillgängliga
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Integrationer med bokföringssystem är ännu inte konfigurerade.
          API-nycklar för Fortnox, Visma och andra leverantörer behöver läggas till i systemet.
        </p>
        <p className="text-xs text-gray-500">
          Se INTEGRATION_SUPPLIERS.md för instruktioner om hur du konfigurerar integrationer.
        </p>
      </div>
    );
  }

  // If guest has an active session, show connected state for that provider
  if (isGuest && guestSession) {
    const config = PROVIDER_CONFIG[guestSession.provider];
    const isSyncing = syncingIntegration === "guest";
    const isLoadingYears = loadingFiscalYears === "guest";

    return (
      <div className="space-y-4">
        <div
          className={`relative rounded-xl border-2 p-6 transition-all ${config.bg} ${config.border}`}
        >
          {/* Connected badge */}
          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
              <CheckCircle size={12} />
              Ansluten
            </span>
          </div>

          <div className="flex items-start gap-4">
            {/* Provider logo */}
            <div
              className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${
                config.logoBg ? config.logoBg : "bg-white"
              } border border-gray-200`}
            >
              <img
                src={config.logo}
                alt={guestSession.provider}
                className="w-10 h-10 object-contain"
              />
            </div>

            {/* Provider info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 capitalize">{guestSession.provider}</h3>

              {/* Connected company */}
              <div className="mt-3 p-3 bg-white/50 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Kopplat företag:</span>
                  <span className="font-medium text-gray-900">
                    {guestSession.companyName || "Ditt Fortnox-konto"}
                  </span>
                </div>
              </div>

              {/* Fortnox Fiscal Year Selector for guests */}
              {showFiscalYearSelector === "guest" && guestSession.provider === "fortnox" && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar size={18} className="text-blue-600" />
                    <span className="font-medium text-blue-900">Välj räkenskapsår att importera</span>
                  </div>
                  {fortnoxFiscalYears.length > 0 ? (
                    <>
                      <div className="relative mb-3">
                        <select
                          value={selectedFiscalYear?.Id || ""}
                          onChange={(e) => {
                            const year = fortnoxFiscalYears.find(y => y.Id === parseInt(e.target.value));
                            setSelectedFiscalYear(year || null);
                          }}
                          className="w-full px-3 py-2 pr-10 border border-blue-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                        >
                          {fortnoxFiscalYears
                            .sort((a, b) => new Date(b.FromDate).getTime() - new Date(a.FromDate).getTime())
                            .map((year) => (
                              <option key={year.Id} value={year.Id}>
                                {year.FromDate} – {year.ToDate}
                              </option>
                            ))}
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleGuestFortnoxSync}
                          disabled={!selectedFiscalYear || isSyncing}
                          className="btn btn-primary btn-sm flex items-center gap-2"
                        >
                          {isSyncing ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Hämtar data...
                            </>
                          ) : (
                            <>
                              <RefreshCw size={14} />
                              Hämta bokföringsdata
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setShowFiscalYearSelector(null);
                            setSelectedFiscalYear(null);
                          }}
                          className="btn btn-ghost btn-sm text-gray-600"
                        >
                          Avbryt
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-blue-700">Inga räkenskapsår hittades i Fortnox.</p>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-4 flex items-center gap-3">
                {showFiscalYearSelector !== "guest" && (
                  <button
                    onClick={() => {
                      if (guestSession.provider === "fortnox") {
                        fetchGuestFortnoxFiscalYears();
                      } else {
                        handleGuestSync();
                      }
                    }}
                    disabled={isSyncing || isLoadingYears}
                    className="btn btn-primary btn-sm flex items-center gap-2"
                  >
                    {isLoadingYears ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Hämtar räkenskapsår...
                      </>
                    ) : isSyncing ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Synkroniserar...
                      </>
                    ) : (
                      <>
                        <RefreshCw size={14} />
                        Hämta bokföringsdata
                      </>
                    )}
                  </button>
                )}

                {/* Disconnect button */}
                <button
                  onClick={handleGuestDisconnect}
                  className="btn btn-ghost btn-sm text-gray-600 flex items-center gap-2"
                >
                  <Unplug size={14} />
                  Koppla bort
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {providers.map((provider) => {
        const integration = getIntegrationForProvider(provider.provider);
        const config = PROVIDER_CONFIG[provider.provider];

        // Safety check - if provider isn't in PROVIDER_CONFIG, skip it
        if (!config) {
          return null;
        }

        const isConnected = integration && integration.status === "active";
        const isConnecting = connectingProvider === provider.provider;
        const isSyncing = integration && syncingIntegration === integration.id;
        const isDisconnecting = integration && disconnectingIntegration === integration.id;

        return (
          <div
            key={provider.provider}
            className={`relative rounded-xl border-2 p-6 transition-all ${
              isConnected
                ? `${config.bg} ${config.border}`
                : provider.available
                  ? "bg-white border-gray-200 hover:border-gray-300"
                  : "bg-gray-50 border-gray-200 opacity-60"
            }`}
          >
            {/* Badge: Connected or Coming Soon */}
            {isConnected && (
              <div className="absolute top-3 right-3">
                <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  <CheckCircle size={12} />
                  Ansluten
                </span>
              </div>
            )}
            {!provider.available && (
              <div className="absolute top-3 right-3">
                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                  Kommer snart
                </span>
              </div>
            )}

            <div className="flex items-start gap-4">
              {/* Provider logo */}
              <div
                className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden ${
                  config.logoBg ? config.logoBg : "bg-white"
                } border border-gray-200`}
              >
                {failedLogos.has(provider.provider) ? (
                  // Fallback icon when image fails to load
                  <Building2 className="w-8 h-8 text-gray-400" />
                ) : (
                  <img
                    src={config.logo}
                    alt={provider.displayName}
                    className="w-10 h-10 object-contain"
                    onError={() => {
                      // Track failed logo and trigger re-render to show fallback
                      setFailedLogos(prev => new Set(prev).add(provider.provider));
                    }}
                  />
                )}
              </div>

              {/* Provider info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{provider.displayName}</h3>
                  {provider.website && (
                    <a
                      href={provider.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <ExternalLink size={14} />
                    </a>
                  )}
                </div>
                <p className="text-sm text-gray-600 mt-1">{provider.description}</p>

                {/* Connected integration details */}
                {integration && integration.status === "active" && (
                  <div className="mt-3 p-3 bg-white/50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500">Kopplat företag:</span>
                      <span className="font-medium text-gray-900">
                        {integration.externalCompanyName || integration.externalCompanyId || "Okänt"}
                      </span>
                    </div>
                    {integration.lastSyncAt && (
                      <div className="flex items-center gap-2 text-sm mt-1">
                        <span className="text-gray-500">Senast synkad:</span>
                        <span className="text-gray-700">
                          {new Date(integration.lastSyncAt).toLocaleString("sv-SE")}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Error state */}
                {integration && integration.status === "error" && (
                  <div className="mt-3 p-3 bg-red-50 rounded-lg flex items-start gap-2">
                    <XCircle className="text-red-500 flex-shrink-0 mt-0.5" size={16} />
                    <div className="text-sm text-red-700">
                      <p className="font-medium">Anslutningsfel</p>
                      <p className="text-red-600">{integration.lastError || "Okänt fel"}</p>
                    </div>
                  </div>
                )}

                {/* Expired state */}
                {integration && integration.status === "expired" && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg flex items-start gap-2">
                    <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={16} />
                    <div className="text-sm text-amber-700">
                      <p className="font-medium">Åtkomst har gått ut</p>
                      <p className="text-amber-600">
                        Din anslutning behöver förnyas. Klicka på "Anslut igen" för att återauktorisera.
                      </p>
                    </div>
                  </div>
                )}

                {/* Fortnox Fiscal Year Selector */}
                {showFiscalYearSelector === integration?.id && provider.provider === "fortnox" && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Calendar size={18} className="text-blue-600" />
                      <span className="font-medium text-blue-900">Välj räkenskapsår att importera</span>
                    </div>
                    {fortnoxFiscalYears.length > 0 ? (
                      <>
                        <div className="relative mb-3">
                          <select
                            value={selectedFiscalYear?.Id || ""}
                            onChange={(e) => {
                              const year = fortnoxFiscalYears.find(y => y.Id === parseInt(e.target.value));
                              setSelectedFiscalYear(year || null);
                            }}
                            className="w-full px-3 py-2 pr-10 border border-blue-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                          >
                            {fortnoxFiscalYears
                              .sort((a, b) => new Date(b.FromDate).getTime() - new Date(a.FromDate).getTime())
                              .map((year) => (
                                <option key={year.Id} value={year.Id}>
                                  {year.FromDate} – {year.ToDate}
                                </option>
                              ))}
                          </select>
                          <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleFortnoxSync(integration.id)}
                            disabled={!selectedFiscalYear || isSyncing}
                            className="btn btn-primary btn-sm flex items-center gap-2"
                          >
                            {isSyncing ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Hämtar data...
                              </>
                            ) : (
                              <>
                                <RefreshCw size={14} />
                                Hämta bokföringsdata
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => {
                              setShowFiscalYearSelector(null);
                              setSelectedFiscalYear(null);
                            }}
                            className="btn btn-ghost btn-sm text-gray-600"
                          >
                            Avbryt
                          </button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-blue-700">Inga räkenskapsår hittades i Fortnox.</p>
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="mt-4 flex items-center gap-3">
                  {isConnected ? (
                    <>
                      {/* Sync button - show loading when fetching fiscal years */}
                      {showFiscalYearSelector !== integration?.id && (
                        <button
                          onClick={() => handleSyncClick(integration.id, provider.provider)}
                          disabled={isSyncing || loadingFiscalYears === integration?.id}
                          className="btn btn-primary btn-sm flex items-center gap-2"
                        >
                          {loadingFiscalYears === integration?.id ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Hämtar räkenskapsår...
                            </>
                          ) : isSyncing ? (
                            <>
                              <Loader2 size={14} className="animate-spin" />
                              Synkroniserar...
                            </>
                          ) : (
                            <>
                              <RefreshCw size={14} />
                              Hämta bokföringsdata
                            </>
                          )}
                        </button>
                      )}

                      {/* Disconnect button */}
                      <button
                        onClick={() => handleDisconnect(integration.id)}
                        disabled={isDisconnecting}
                        className="btn btn-ghost btn-sm text-gray-600 flex items-center gap-2"
                      >
                        {isDisconnecting ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Kopplar bort...
                          </>
                        ) : (
                          <>
                            <Unplug size={14} />
                            Koppla bort
                          </>
                        )}
                      </button>
                    </>
                  ) : integration?.status === "expired" ? (
                    <button
                      onClick={() => handleConnect(provider.provider)}
                      disabled={isConnecting}
                      className="btn btn-primary btn-sm flex items-center gap-2"
                    >
                      {isConnecting ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Ansluter...
                        </>
                      ) : (
                        <>
                          <RefreshCw size={14} />
                          Anslut igen
                        </>
                      )}
                    </button>
                  ) : provider.available ? (
                    <button
                      onClick={() => handleConnect(provider.provider)}
                      disabled={isConnecting}
                      className="btn btn-primary btn-sm flex items-center gap-2"
                    >
                      {isConnecting ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Ansluter...
                        </>
                      ) : (
                        <>
                          <Zap size={14} />
                          Anslut {provider.displayName}
                        </>
                      )}
                    </button>
                  ) : (
                    <span className="text-sm text-gray-500">
                      Integration kommer snart
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
